import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

import MarkdownPreview from "../../../../../../components/MarkdownPreview";
import {
  EQUIPMENT_DISTRIBUTION,
  EQUIPMENT_TYPE_OPTIONS,
  INPUT_CATEGORY_OPTIONS,
  INPUT_DISTRIBUTION,
  type DistributionOptionDef,
} from "../../constants";
import type { AggregatorOption, InfoHubStepProps, TopicOption } from "../../types";

export const InfoHubReviewStep = ({ methods }: InfoHubStepProps) => {
  const { t } = useTranslation();
  const { watch } = methods;
  const values = watch();

  const topics = (values.topics as TopicOption[] | undefined) ?? [];
  const aggregators = (values.aggregators as AggregatorOption[] | undefined) ?? [];

  const contentTypeValue = values.content_type ? String(values.content_type.value) : undefined;
  // Resolve a choice code to its translated label (values stored on edit carry
  // the code as label, so always translate from the option defs).
  const optionLabel = (defs: DistributionOptionDef[], code?: number | string) => {
    const def = defs.find(option => option.value === code);
    return def ? t(def.labelKey) : (code ?? "");
  };

  const detailRow = (label: string, value?: ReactNode) =>
    value ? (
      <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-4">
        <span className="font-semibold text-muted-foreground sm:w-48 sm:shrink-0">{label}</span>
        <span className="text-foreground">{value}</span>
      </div>
    ) : null;

  return (
    <div className="flex flex-col gap-4 text-sm">
      <div>
        {values.content_type?.label ? (
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {values.content_type.label}
          </div>
        ) : null}
        <h3 className="mt-1 text-lg font-semibold text-foreground">{values.name}</h3>
      </div>

      {topics.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {topics.map((topic, index) => (
            <Badge key={`tag-${index}`} variant="secondary">
              {topic.label}
            </Badge>
          ))}
        </div>
      )}

      <MarkdownPreview source={values.content as string} />

      {contentTypeValue === INPUT_DISTRIBUTION && (
        <div className="flex flex-col gap-2">
          {detailRow(
            t("infoHub.form.inputCategory"),
            optionLabel(INPUT_CATEGORY_OPTIONS, values.input_distribution_category?.value)
          )}
          {detailRow(t("infoHub.form.inputDescription"), values.input_distribution_details)}
          {detailRow(
            t("infoHub.form.quantity"),
            values.input_distribution_quantity != null
              ? `${values.input_distribution_quantity}${
                  values.input_distribution_unit_of_measure
                    ? ` ${values.input_distribution_unit_of_measure.label}`
                    : ""
                }`
              : undefined
          )}
        </div>
      )}

      {contentTypeValue === EQUIPMENT_DISTRIBUTION && (
        <div className="flex flex-col gap-2">
          {detailRow(
            t("infoHub.form.equipmentType"),
            optionLabel(EQUIPMENT_TYPE_OPTIONS, values.equipment_distribution_type?.value)
          )}
          {detailRow(t("infoHub.form.equipmentDescription"), values.equipment_distribution_details)}
          {detailRow(t("infoHub.form.quantity"), values.equipment_distribution_quantity)}
        </div>
      )}

      <div className="border-t border-border" />

      <div className="flex flex-col gap-2">
        {aggregators.length > 0 && (
          <div className="font-semibold text-foreground">{t("infoHub.form.sharedWith")}</div>
        )}
        {aggregators.length > 0 ? (
          <div className="flex flex-col gap-1 text-muted-foreground">
            {aggregators.map((aggregator, index) => (
              <span key={`tag-${index}`}>
                {aggregator.label || t("infoHub.form.unknownAggregator", { id: aggregator.value })}
              </span>
            ))}
          </div>
        ) : (
          <Alert variant="warning">
            <AlertDescription>{t("infoHub.form.noAggregator")}</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};
